/* El motor de FiboSheets: fórmulas estilo Excel/LibreOffice, sin librería.
 *
 * ── QUÉ RESUELVE ───────────────────────────────────────────────────────────
 *
 * Una planilla es una matriz de celdas (texto crudo). Una celda que empieza con
 * "=" es una fórmula. El motor entiende NÚMEROS, TEXTO y BOOLEANOS, con:
 *   - referencias (A1), rangos (A1:B3), columnas y filas enteras (A:A, 3:5),
 *     y referencias fijas con $ ($A$1, A$1);
 *   - aritmética (+ - * / ^, paréntesis, menos unario, y % como en =A1*21%);
 *   - texto: concatenación con & y comillas ("hola");
 *   - comparaciones (= <> < > <= >=) que devuelven VERDADERO/FALSO;
 *   - funciones: matemáticas, lógicas (SI, Y, O, NO, SI.ERROR), condicionales
 *     (SUMAR.SI, CONTAR.SI, PROMEDIO.SI y sus .CONJUNTO), búsqueda (BUSCARV,
 *     BUSCARX, INDICE, COINCIDIR), texto, fecha y hora;
 *   - comodines * ? ~ en los criterios y en las búsquedas exactas;
 *   - números escritos a la argentina («$ 1.234,50», «45.000»: ver
 *     numeroLiteral) y horas «08:30» que se suman y restan (ver «Horas»).
 * Cada función tiene su alias en inglés. Los ciclos se marcan #CICLO en vez de
 * colgar; los errores (#DIV/0, #VALOR, #REF, #N/D, #NOMBRE) se propagan.
 *
 * ── LOS DATOS DEL HUB (HUB.…) ──────────────────────────────────────────────
 *
 * Igna, 04/10/2026: fórmulas que traen datos vivos del hub, como
 * =HUB.PRECIO_GASOIL(). El motor NO sabe de dónde salen: no lee la base, no
 * importa nada (tiene que seguir andando solo en Deno, ver la edge function
 * `evaluar-planilla`). Quien quiera esos datos le pasa `opciones.hub`, un
 * objeto con `leer(nombre, args)` que contesta en el acto (es síncrono):
 *   { valor }            el dato, ya en la mano;
 *   { cargando: true }   lo está yendo a buscar → la celda vale CARGANDO;
 *   { error, motivo }    no se puede → la celda vale ese error (#HUB, #N/D…).
 * Sin `opciones.hub`, toda HUB.… vale #HUB: así se calcula en el servidor,
 * donde no hay sesión de nadie y no se puede leer el hub en su nombre.
 *
 * CARGANDO se propaga como un error (lo que depende de un dato que no llegó
 * tampoco tiene valor), pero SI.ERROR y ESERROR no lo atrapan: «todavía no
 * llegó» no es «falló», y si no, el SI.ERROR mostraría su plan B un segundo.
 *
 * `opciones.motivos` (un Map, opcional) junta el porqué de cada error que lo
 * trae, por referencia de la hoja calculada ("B3" → «hace falta el permiso…»):
 * es lo que la pantalla muestra al pasar el mouse.
 *
 * ── POR QUÉ PROPIO Y NO UNA LIBRERÍA ───────────────────────────────────────
 *
 * La referencia de "cómo funciona cada función" es pública (la spec OpenFormula
 * de ODF, que LibreOffice implementa). Pero copiar el código de LibreOffice
 * (LGPL) ataría el hub a esa licencia; se implementa acá, desde la spec, que es
 * limpio. El motor es puro y se testea entero.
 */

/* ── Referencias de celda ─────────────────────────────────────────────────── */

export function colALetra(n) {
  let s = "";
  let x = n;
  do {
    s = String.fromCharCode(65 + (x % 26)) + s;
    x = Math.floor(x / 26) - 1;
  } while (x >= 0);
  return s;
}

export function letraACol(letra) {
  let n = 0;
  for (const ch of letra.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

export function parseRef(ref) {
  // El $ fija la referencia al copiar o rellenar; para calcular no cambia nada.
  const m = /^\$?([A-Za-z]+)\$?(\d+)$/.exec(String(ref).trim());
  if (!m) return null;
  return { fila: parseInt(m[2], 10) - 1, col: letraACol(m[1]) };
}

export function refDe(fila, col) {
  return `${colALetra(col)}${fila + 1}`;
}

export function matrizVacia(filas, cols) {
  return Array.from({ length: filas }, () => Array.from({ length: cols }, () => ""));
}

/* Un texto que es un número, escrito como se escribe acá o como lo exporta un
 * sistema. Devuelve el número, o null. LA REGLA (mejora #5 de FiboSheets, 05/10/2026):
 *
 *   - La coma es SIEMPRE decimal: 12,5 · 0,75 · 1.234,56.
 *   - El punto es DE MILES cuando arma grupos de tres cifras detrás de un
 *     primer grupo de una a tres que no empieza con 0: 1.234 · 45.000 ·
 *     1.234.567 · 1.234.567,89. Así lo lee cualquiera acá: «45.000» son
 *     cuarenta y cinco mil, no cuarenta y cinco (antes se leía 45, y un «1.234»
 *     era 1,234: la planilla sumaba mal callada).
 *   - Si no, el punto es decimal, como lo exporta un sistema: 3.14 · 1234.5 ·
 *     0.500 · 12.75 · 1.2345.
 *   - El formato yanqui sólo cuando no hay duda, con los dos signos y la coma
 *     antes del punto: 1,234.56. «1,234» solo es uno coma dos tres cuatro.
 *   - Adelante puede ir un $ y un menos, en cualquier orden y con espacios
 *     («$ 1.234,50», «-$ 500», «$-500»); atrás, un % que divide por cien
 *     («12,5%» es 0,125). Nada más: «12,5 kg» o un CUIT siguen siendo texto.
 *
 * Lo que se pierde: un sistema que exporte con punto decimal y justo tres
 * decimales («1.250» kilos) se lee como mil doscientos cincuenta. Es el precio
 * de leer como se escribe acá; el import de Excel escribe esos con coma (ver
 * `textoDe` en planillaExcel.js). Los números ESCRITOS EN UNA FÓRMULA (=A1*1.5)
 * no pasan por acá: ahí el punto es decimal, como en Excel en inglés. */
const RE_NUMERO_AR = /^(-?)\s*(\$?)\s*(-?)\s*([\d.,]+)\s*(%?)$/;
export function numeroLiteral(txt) {
  const m = RE_NUMERO_AR.exec(String(txt).trim());
  if (!m || (m[1] && m[3]) || (m[2] && m[5])) return null;
  const c = m[4];
  let n = null;
  if (/^\d+$/.test(c)) n = parseFloat(c);
  else if (/^\d+,\d+$/.test(c)) n = parseFloat(c.replace(",", "."));
  else if (/^[1-9]\d{0,2}(\.\d{3})+(,\d+)?$/.test(c)) n = parseFloat(c.replace(/\./g, "").replace(",", "."));
  else if (/^\d+\.\d+$/.test(c)) n = parseFloat(c);
  else if (/^[1-9]\d{0,2}(,\d{3})+\.\d+$/.test(c)) n = parseFloat(c.replace(/,/g, ""));
  if (n === null) return null;
  if (m[1] || m[3]) n = -n;
  return m[5] ? n / 100 : n;
}

/* Si un texto que ES número se puede mostrar como número sin que la celda
 * cambie de cara. Lo que ya se leía como número antes de la regla de arriba
 * (1234,5 · 1.234.567 · 1.234,56) sigue siéndolo; lo nuevo («$ 1.234,50»,
 * «45.000», «12,5%») se calcula como número pero la celda muestra lo que
 * escribió la persona: si no, «$ 1.234,50» se vería «1234,5». */
function esNumeroPlano(txt) {
  const s = String(txt).trim();
  if (/^-?[1-9]\d{0,2}\.\d{3}$/.test(s)) return false; // 1.234: antes 1,234, ahora mil y pico
  return /^-?\d+([.,]\d+)?$/.test(s) || /^-?\d{1,3}(\.\d{3})+,\d+$/.test(s) || /^-?\d{1,3}(\.\d{3}){2,}$/.test(s);
}

/* Columnas y filas enteras, como en Excel: A:A, $B:$D, 3:3, 1:5. Lo que sigue
 * no puede ser parte de un nombre o de una celda (A:B5 no es una columna). */
const RE_COLS = /^\$?([A-Za-z]{1,3}):\$?([A-Za-z]{1,3})(?![\p{L}\p{N}_.$!(])/u;
const RE_FILAS = /^\$?(\d{1,7}):\$?(\d{1,7})(?![\p{L}\p{N}_.$!(])/u;

/* Lo que va después de Hoja! (o lo que arranca un nombre suelto): una
 * columna o fila entera, o una celda. null si no es ninguna. */
function refAca(resto, hoja) {
  let mm = RE_COLS.exec(resto);
  if (mm) return { tk: { t: "entero", eje: "col", a: mm[1], b: mm[2], hoja }, largo: mm[0].length };
  mm = RE_FILAS.exec(resto);
  if (mm) return { tk: { t: "entero", eje: "fila", a: mm[1], b: mm[2], hoja }, largo: mm[0].length };
  if (hoja == null) return null; // sin hoja, la celda la lee el tokenizer como nombre
  mm = /^\$?[A-Za-z]+\$?\d+/.exec(resto);
  return mm ? { tk: { t: "ref", v: mm[0], hoja }, largo: mm[0].length } : null;
}

/* ── Tokenizer ─────────────────────────────────────────────────────────────
 * Números, textos "…", identificadores (refs y funciones — el '.' se admite
 * para nombres como SUMAR.SI y SI.ERROR), operadores, comparadores, & y : */
function tokenizar(s) {
  const t = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (c === " " || c === "\t") { i += 1; continue; }
    if (c === '"') {
      let j = i + 1;
      let str = "";
      while (j < s.length) {
        if (s[j] === '"') {
          if (s[j + 1] === '"') { str += '"'; j += 2; continue; }
          j += 1; break;
        }
        str += s[j]; j += 1;
      }
      t.push({ t: "str", v: str }); i = j; continue;
    }
    if ((c >= "0" && c <= "9") || c === "$" || /[A-Za-z]/.test(c)) {
      const ent = refAca(s.slice(i), null); // 3:3, A:A, $A:$C
      if (ent) { t.push(ent.tk); i += ent.largo; continue; }
    }
    if (c >= "0" && c <= "9") {
      let j = i + 1;
      while (j < s.length && ((s[j] >= "0" && s[j] <= "9") || s[j] === ".")) j += 1;
      t.push({ t: "num", v: parseFloat(s.slice(i, j)) }); i = j; continue;
    }
    // Nombre de hoja entre comillas simples, para referencias entre hojas cuyo
    // nombre tiene espacios o caracteres raros: 'Mi Hoja'!A1  (el '' escapa una
    // comilla dentro del nombre, como en Excel).
    if (c === "'") {
      let j = i + 1;
      let nombre = "";
      while (j < s.length) {
        if (s[j] === "'") { if (s[j + 1] === "'") { nombre += "'"; j += 2; continue; } j += 1; break; }
        nombre += s[j]; j += 1;
      }
      if (s[j] !== "!") throw new Error("se esperaba ! después del nombre de hoja");
      const r = refAca(s.slice(j + 1), nombre);
      if (!r) throw new Error("referencia inválida después de !");
      t.push(r.tk);
      i = j + 1 + r.largo; continue;
    }
    // Un error escrito en la fórmula (#REF! es lo que queda cuando se borra la
    // fila o la columna a la que apuntaba): vale como ese error.
    if (c === "#") {
      const mm = /^#[A-Za-z0-9/]+[!?]?/.exec(s.slice(i));
      if (mm) { t.push({ t: "err", v: mm[0].replace(/[!?]$/, "").toUpperCase() }); i += mm[0].length; continue; }
    }
    if (/[\p{L}_$]/u.test(c)) { // letras (incluye ñ y acentos, p.ej. AÑO) y el $ de $A$1
      let j = i + 1;
      while (j < s.length && /[\p{L}\p{N}_.$]/u.test(s[j])) j += 1;
      const nom = s.slice(i, j);
      // Referencia a otra hoja sin comillas: Enero!A1 (lo leído es el nombre de
      // la hoja, y lo que sigue al ! es la celda).
      if (s[j] === "!") {
        const r = refAca(s.slice(j + 1), nom);
        if (!r) throw new Error("referencia inválida después de !");
        t.push(r.tk);
        i = j + 1 + r.largo; continue;
      }
      let k = j;
      while (k < s.length && s[k] === " ") k += 1;
      t.push(s[k] === "(" ? { t: "fn", v: nom } : { t: "ref", v: nom, hoja: null });
      i = j; continue;
    }
    if (c === "<" && s[i + 1] === "=") { t.push({ t: "cmp", v: "<=" }); i += 2; continue; }
    if (c === ">" && s[i + 1] === "=") { t.push({ t: "cmp", v: ">=" }); i += 2; continue; }
    if (c === "<" && s[i + 1] === ">") { t.push({ t: "cmp", v: "<>" }); i += 2; continue; }
    if ("=<>".includes(c)) { t.push({ t: "cmp", v: c }); i += 1; continue; }
    if (c === "&") { t.push({ t: "&" }); i += 1; continue; }
    if ("+-*/^()%".includes(c)) { t.push({ t: c }); i += 1; continue; }
    if (c === "," || c === ";") { t.push({ t: "sep" }); i += 1; continue; }
    if (c === ":") { t.push({ t: ":" }); i += 1; continue; }
    throw new Error("caracter inesperado: " + c);
  }
  return t;
}

/* ── Parser (descenso recursivo) ───────────────────────────────────────────
 * Precedencia (de menor a mayor): comparación < concatenación (&) < ± < * / <
 * potencia < unario < primario. */
function parsear(toks) {
  let p = 0;
  const ver = () => toks[p];
  const comer = (tt) => {
    const x = toks[p];
    if (!x || (tt && x.t !== tt)) throw new Error("se esperaba " + tt);
    p += 1; return x;
  };

  function comparacion() {
    let izq = concat();
    while (ver() && ver().t === "cmp") { const op = comer().v; izq = { tipo: "cmp", op, izq, der: concat() }; }
    return izq;
  }
  function concat() {
    let izq = aditivo();
    while (ver() && ver().t === "&") { comer("&"); izq = { tipo: "cat", izq, der: aditivo() }; }
    return izq;
  }
  function aditivo() {
    let izq = multiplicativo();
    while (ver() && (ver().t === "+" || ver().t === "-")) { const op = comer().t; izq = { tipo: "op", op, izq, der: multiplicativo() }; }
    return izq;
  }
  function multiplicativo() {
    let izq = potencia();
    while (ver() && (ver().t === "*" || ver().t === "/")) { const op = comer().t; izq = { tipo: "op", op, izq, der: potencia() }; }
    return izq;
  }
  function potencia() {
    const izq = unario();
    if (ver() && ver().t === "^") { comer("^"); return { tipo: "op", op: "^", izq, der: potencia() }; }
    return izq;
  }
  function unario() {
    const tk = ver();
    if (tk && tk.t === "-") { comer("-"); return { tipo: "neg", arg: unario() }; }
    if (tk && tk.t === "+") { comer("+"); return unario(); }
    // El % va pegado a lo que tiene a la izquierda: 21% es 0,21.
    let n = primario();
    while (ver() && ver().t === "%") { comer("%"); n = { tipo: "pct", arg: n }; }
    return n;
  }
  function primario() {
    const tk = ver();
    if (!tk) throw new Error("expresión incompleta");
    if (tk.t === "num") { comer("num"); return { tipo: "num", v: tk.v }; }
    if (tk.t === "str") { comer("str"); return { tipo: "str", v: tk.v }; }
    if (tk.t === "err") { comer("err"); return { tipo: "err", v: tk.v }; }
    if (tk.t === "(") { comer("("); const e = comparacion(); comer(")"); return e; }
    if (tk.t === "fn") {
      comer("fn"); comer("(");
      const args = [];
      if (ver() && ver().t !== ")") {
        args.push(comparacion());
        while (ver() && ver().t === "sep") { comer("sep"); args.push(comparacion()); }
      }
      comer(")");
      return { tipo: "fn", nombre: tk.v.toUpperCase(), args };
    }
    // Una columna o fila entera es un rango; `eje` le dice a rangoCeldas que
    // la acote a la parte usada del libro.
    if (tk.t === "entero") { comer("entero"); return { tipo: "rango", hoja: tk.hoja ?? null, a: tk.a, b: tk.b, eje: tk.eje }; }
    if (tk.t === "ref") {
      comer("ref");
      // El nombre de hoja (si lo hay) lo aporta la PRIMERA esquina: en
      // `Enero!A1:B2` las dos celdas son de Enero.
      if (ver() && ver().t === ":") { comer(":"); const b = comer("ref"); return { tipo: "rango", hoja: tk.hoja ?? null, a: tk.v, b: b.v }; }
      return { tipo: "ref", v: tk.v, hoja: tk.hoja ?? null };
    }
    throw new Error("token inesperado");
  }

  const arbol = comparacion();
  if (p !== toks.length) throw new Error("sobra texto en la fórmula");
  return arbol;
}

/* ── Evaluación ───────────────────────────────────────────────────────────── */

const VERDAD = { VERDADERO: true, TRUE: true, FALSO: false, FALSE: false };

/* Lo que vale una celda HUB.… mientras el dato viaja, y lo que vale cuando no
   se puede traer (o se calcula donde no hay hub). */
export const CARGANDO = "#CARGANDO";
export const ERROR_HUB = "#HUB";

/* Un error de celda. `motivo` (opcional) es el porqué, para mostrarlo. */
class ErrCelda extends Error {
  constructor(codigo, motivo = null) {
    super(codigo);
    this.motivo = motivo;
  }
}

function limpiar(x) {
  if (typeof x !== "number" || !Number.isFinite(x)) return x;
  return Math.round(x * 1e10) / 1e10;
}

/* ── Redondeo como Excel ────────────────────────────────────────────────────
 * `Math.round(x * 100) / 100` falla dos veces: 1,005 en binario es
 * 1,00499999…, así que REDONDEAR(1,005;2) daba 1 y no 1,01; y Math.round
 * redondea la mitad hacia +∞, así que REDONDEAR(-2,5;0) daba -2 y no -3.
 * Excel mira el número como lo ve la persona (15 cifras) y la mitad se ALEJA
 * del cero. Acá igual: se recorta a 15 cifras significativas y la coma se
 * corre escribiendo el exponente en el texto («1.005e2» → 100,5 exacto), sin
 * multiplicar en binario. REDONDEAR.MAS/MENOS y TRUNCAR usan lo mismo. */
function correrComa(x, d) {
  const [m, e] = String(x).split("e");
  return Number(`${m}e${(Number(e) || 0) + d}`);
}
function redondearComo(x, digitos, modo) {
  if (!Number.isFinite(x)) return x;
  const d = Math.trunc(digitos) || 0;
  const visto = Number(x.toPrecision(15));
  const corrido = correrComa(Math.abs(visto), d);
  if (!Number.isFinite(corrido)) return visto; // más decimales de los que hay
  const ent = Math.trunc(corrido);
  const resto = corrido - ent; // exacto: sacarle la parte entera no redondea
  let r = ent;
  if (modo === "mitad") r = resto >= 0.5 ? ent + 1 : ent;
  else if (modo === "arriba") r = resto > 0 ? ent + 1 : ent;
  const firmado = visto < 0 ? -correrComa(r, -d) : correrComa(r, -d);
  return firmado + 0; // sin -0
}

function dosDig(n) { return String(n).padStart(2, "0"); }
/* HOY() y AHORA() en hora ARGENTINA, no en la de la máquina: en la edge
   function (Deno) el reloj es UTC y «hoy» pasaba al día siguiente a las 21:00.
   UTC-3 fijo, como `shared/fechaAr.js` (el motor no importa nada, por eso la
   cuenta va acá): el instante corrido tres horas y leído con getUTC*. */
const TZ_AR_MS = -3 * 3600 * 1000;
function fechaHoy(conHora) {
  const d = new Date(Date.now() + TZ_AR_MS);
  const f = `${dosDig(d.getUTCDate())}/${dosDig(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
  return conHora ? `${f} ${dosDig(d.getUTCHours())}:${dosDig(d.getUTCMinutes())}` : f;
}

/* Las fechas viajan como TEXTO legible (DD/MM/YYYY) en la celda, pero para la
   aritmética se convierten a un "serial" (días desde 1899-12-30, como Excel), y
   así `fecha - fecha` da los días y `fecha + n` corre la fecha. */
const EPOCH_FECHA = Date.UTC(1899, 11, 30);
export function fechaASerial(s) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(s).trim());
  if (!m) return null;
  const d = +m[1];
  const mo = +m[2];
  const y = +m[3];
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const ms = Date.UTC(y, mo - 1, d);
  const dt = new Date(ms);
  if (dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null; // 31/02, etc.
  return Math.round((ms - EPOCH_FECHA) / 86400000);
}
export function serialAFecha(serial) {
  const d = new Date(EPOCH_FECHA + Math.round(serial) * 86400000);
  return `${dosDig(d.getUTCDate())}/${dosDig(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
}
function partesFecha(serial) {
  // Hacia abajo y no redondeando: 21/09/2026 18:00 (…,75) sigue siendo el 21.
  const d = new Date(EPOCH_FECHA + Math.floor(serial + 1e-9) * 86400000);
  return { a: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(), sem: d.getUTCDay() };
}

/* ── Horas ──────────────────────────────────────────────────────────────────
 * Mejora #5 de FiboSheets (05/10/2026). Como las fechas: viajan como TEXTO legible
 * («08:30», «08:30:15», «21/09/2026 08:30») y para calcular valen lo que en
 * Excel, la fracción del día: 08:30 es 0,354166… (8,5 / 24). Así se suman,
 * se restan y se comparan, y HORA/MINUTO las desarman.
 *
 * Una hora suelta también es una DURACIÓN: puede pasar de 24 («36:00», el
 * total de horas de la semana) o ser negativa («-01:30», salió antes). */
export function horaASerial(s) {
  const m = /^(-)?(\d{1,4}):([0-5]\d)(?::([0-5]\d))?$/.exec(String(s).trim());
  if (!m) return null;
  const seg = (+m[2]) * 3600 + (+m[3]) * 60 + (+(m[4] || 0));
  return (m[1] ? -seg : seg) / 86400;
}
/* La fracción de día como «hh:mm» (o «hh:mm:ss» si hay segundos), al segundo.
   Las horas no dan la vuelta en 24: es el [h]:mm de Excel. */
export function serialAHora(x) {
  const seg = Math.round(Math.abs(x) * 86400);
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  return `${x < 0 && seg ? "-" : ""}${dosDig(h)}:${dosDig(m)}${s ? ":" + dosDig(s) : ""}`;
}
function serialAFechaHora(x) {
  const seg = Math.round(x * 86400);
  const dias = Math.floor(seg / 86400);
  return `${serialAFecha(dias)} ${serialAHora((seg - dias * 86400) / 86400)}`;
}
/* Una fecha, una fecha con hora o una hora escrita como texto → { serial,
   tipo: "fecha" | "fechahora" | "hora" }, o null si no es ninguna. */
function textoASerial(v) {
  if (typeof v !== "string") return null;
  const s = v.trim();
  const f = fechaASerial(s);
  if (f !== null) return { serial: f, tipo: "fecha" };
  const fh = /^(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{1,2}:[0-5]\d(?::[0-5]\d)?)$/.exec(s);
  if (fh) {
    const dia = fechaASerial(fh[1]);
    const hora = horaASerial(fh[2]);
    if (dia !== null && hora < 1) return { serial: dia + hora, tipo: "fechahora" };
    return null;
  }
  const h = horaASerial(s);
  return h === null ? null : { serial: h, tipo: "hora" };
}
/* Lo que da sumar o restar dos cosas con hora, para que se pueda mostrar:
 *   hora ± hora                 → una hora (08:30 + 01:15 = 09:45);
 *   fecha(+hora) ± hora         → fecha con hora (salió el 21/09 22:00 + 05:30);
 *   fecha con hora − fecha(+h)  → la duración, como hora («50:30» de viaje).
 * Lo demás queda número: fecha − fecha son días, como siempre, y una hora por
 * un número (08:30*24 = 8,5 horas, para multiplicar por el valor hora) también. */
function conHora(res, op, va, vb) {
  const ta = textoASerial(va)?.tipo;
  const tb = textoASerial(vb)?.tipo;
  if (!ta || !tb) return res;
  const conDia = (t) => t === "fecha" || t === "fechahora";
  if (ta === "hora" && tb === "hora") return serialAHora(res);
  if (conDia(ta) && tb === "hora") return serialAFechaHora(res);
  if (op === "+" && ta === "hora" && conDia(tb)) return serialAFechaHora(res);
  if (op === "-" && conDia(ta) && conDia(tb) && (ta === "fechahora" || tb === "fechahora")) return serialAHora(res);
  return res;
}

/* ── Comodines (CONTAR.SI, SUMAR.SI, BUSCARV exacto…) ─────────────────────
 * Como en Excel: * es cualquier cosa (también nada), ? es un carácter, y ~
 * delante de * ? ~ los vuelve literales («~*» es un asterisco). Sin distinguir
 * mayúsculas. Devuelve la expresión regular, o null si el patrón no tiene
 * ninguno de los tres (entonces se compara como siempre). */
function comodin(patron) {
  if (typeof patron !== "string" || !/[*?~]/.test(patron)) return null;
  let re = "";
  for (let i = 0; i < patron.length; i += 1) {
    const ch = patron[i];
    if (ch === "~" && i + 1 < patron.length && "*?~".includes(patron[i + 1])) { re += patron[i + 1] === "~" ? "~" : "\\" + patron[i + 1]; i += 1; }
    else if (ch === "*") re += "[\\s\\S]*";
    else if (ch === "?") re += "[\\s\\S]";
    else re += ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  return new RegExp("^" + re + "$", "iu");
}
/* Un comodín sólo calza con TEXTO, como en Excel: "1*" no cuenta el 15, y
   "*" no cuenta las vacías. */
const calzaComodin = (re, v) => typeof v === "string" && v !== "" && !v.startsWith("#") && re.test(v);

/* Evalúa una hoja y devuelve su matriz de valores calculados.
 *
 * `opciones.hojas` (opcional) es el LIBRO entero — `[{ nombre, celdas }]` — y
 * habilita las referencias ENTRE hojas (`=SUMA(Enero!A1:A10)`). `opciones.hojaActual`
 * es el nombre de la hoja que se está calculando (la de `celdas`). Sin el libro,
 * funciona igual que antes sobre una sola hoja y una referencia a otra hoja da
 * #REF (no hay otras hojas que conocer).
 *
 * El memo y la detección de ciclos abarcan TODAS las hojas (la clave lleva la
 * hoja), así que un ciclo Total!A1→Enero!A1→Total!A1 se marca #CICLO igual.
 *
 * `opciones.hub` y `opciones.motivos`: ver «LOS DATOS DEL HUB» arriba. */
export function evaluar(celdas, opciones = {}) {
  const libro = Array.isArray(opciones.hojas) ? opciones.hojas : null;
  const hub = opciones.hub && typeof opciones.hub.leer === "function" ? opciones.hub : null;
  const motivos = opciones.motivos instanceof Map ? opciones.motivos : null;
  // nombre de hoja (en minúsculas) → sus celdas.
  const mapa = new Map();
  if (libro) {
    for (const h of libro) {
      if (h && h.nombre != null) mapa.set(String(h.nombre).toLowerCase(), h.celdas || []);
    }
  }
  // La hoja que se devuelve calculada. "" es la hoja anónima (llamada sin libro).
  const claveActual = opciones.hojaActual != null ? String(opciones.hojaActual).toLowerCase() : "";
  const celdasDe = (clave) => (clave === "" ? celdas : (mapa.get(clave) || null));

  // Dimensiones por hoja, cacheadas (Math.max recorre las filas: no repetirlo
  // por cada celda leída).
  const dimsCache = new Map();
  const dims = (clave) => {
    if (dimsCache.has(clave)) return dimsCache.get(clave);
    const cel = celdasDe(clave);
    const filas = cel ? cel.length : 0;
    const cols = filas ? Math.max(0, ...cel.map((f) => f.length)) : 0;
    const d = { filas, cols, cel };
    dimsCache.set(clave, d);
    return d;
  };

  const memo = new Map();
  // Lo que MUESTRA una celda escrita como «$ 1.234,50» o «45.000»: para
  // calcular vale el número (en `memo`), pero se ve como la escribió la
  // persona. Ver esNumeroPlano.
  const vistas = new Map();
  const enCurso = new Set();
  // Qué hoja está corriendo: una referencia SIN hoja (A1) es de acá; una CON
  // hoja (Enero!A1) va a esa otra. Se guarda y restaura al bajar a otra celda.
  let hojaCtx = claveActual;

  function valor(clave, r, c) {
    const { filas, cols, cel } = dims(clave);
    if (cel == null) throw new ErrCelda("#REF"); // la hoja no existe
    if (r < 0 || c < 0 || r >= filas || c >= cols) return "";
    const k = clave + "|" + r + "," + c;
    if (memo.has(k)) return memo.get(k);
    if (enCurso.has(k)) throw new ErrCelda("#CICLO");
    const txt = (cel[r] && cel[r][c] != null ? String(cel[r][c]) : "").trim();
    let res;
    if (txt.startsWith("=")) {
      enCurso.add(k);
      const prev = hojaCtx;
      hojaCtx = clave;
      try {
        res = evalNodo(parsear(tokenizar(txt.slice(1))));
        if (typeof res === "number") res = limpiar(res);
      } catch (e) {
        res = e instanceof ErrCelda ? e.message : "#ERROR";
        if (motivos && e instanceof ErrCelda && e.motivo && clave === claveActual) motivos.set(refDe(r, c), e.motivo);
      } finally {
        enCurso.delete(k);
        hojaCtx = prev;
      }
    } else if (txt === "") {
      res = "";
    } else {
      const n = numeroLiteral(txt);
      res = n === null ? txt : n;
      if (n !== null && !esNumeroPlano(txt)) vistas.set(k, txt);
    }
    memo.set(k, res);
    return res;
  }

  // La hoja (clave) que le toca a una referencia/rango: la suya si la nombra, la
  // del formula en curso si no.
  const claveDe = (hojaNodo) => (hojaNodo == null ? hojaCtx : String(hojaNodo).toLowerCase());

  /* Un valor que es un error (#…) no se usa: se propaga. */
  const chk = (v) => { if (typeof v === "string" && v.startsWith("#")) throw new ErrCelda(v); return v; };
  const aNum = (v) => {
    v = chk(v);
    if (v === "") return 0;
    if (typeof v === "number") return v;
    if (typeof v === "boolean") return v ? 1 : 0;
    const s = String(v).trim();
    // "12,5" o "45.000" que llegó como texto (de un & o un EXTRAE): con la
    // regla de acá, antes que la de JavaScript (que lee "45.000" como 45).
    const lit = numeroLiteral(s);
    if (lit !== null) return lit;
    if (s !== "" && !Number.isNaN(Number(s))) return Number(s);
    const ts = textoASerial(s); // una fecha, fecha con hora u hora cuenta como su serial
    if (ts !== null) return ts.serial;
    throw new ErrCelda("#VALOR");
  };
  const aTxt = (v) => {
    v = chk(v);
    if (v === "") return "";
    if (typeof v === "boolean") return v ? "VERDADERO" : "FALSO";
    if (typeof v === "number") return String(limpiar(v));
    return String(v);
  };
  const aBool = (v) => {
    v = chk(v);
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    const s = String(v).trim().toUpperCase();
    if (s in VERDAD) return VERDAD[s];
    if (s !== "" && !Number.isNaN(Number(s))) return Number(s) !== 0;
    throw new ErrCelda("#VALOR");
  };

  /* -1 / 0 / 1. Números por valor; el resto como texto sin distinguir may/min. */
  const comparar = (a, b) => {
    a = chk(a); b = chk(b);
    if (typeof a === "number" && typeof b === "number") return a < b ? -1 : a > b ? 1 : 0;
    if (typeof a === "boolean" && typeof b === "boolean") return a === b ? 0 : a ? 1 : -1;
    // Fechas y horas, por lo que valen y no como texto ("09:15" > "10:00"
    // no, aunque "20/12" < "21/01" en texto); contra un número, también.
    const ta = textoASerial(a);
    const tb = textoASerial(b);
    const na = ta ? ta.serial : typeof a === "number" ? a : null;
    const nb = tb ? tb.serial : typeof b === "number" ? b : null;
    if ((ta || tb) && na !== null && nb !== null) return na < nb ? -1 : na > nb ? 1 : 0;
    const sa = aTxt(a).toUpperCase();
    const sb = aTxt(b).toUpperCase();
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  };
  const cumpleCmp = (r, op) => {
    switch (op) {
      case "=": return r === 0;
      case "<>": return r !== 0;
      case "<": return r < 0;
      case ">": return r > 0;
      case "<=": return r <= 0;
      case ">=": return r >= 0;
      default: throw new ErrCelda("#ERROR");
    }
  };

  /* Hasta dónde llega una columna o fila entera: hasta la última fila (o
     columna) que tiene ALGUNA hoja del libro. No la de esa hoja sola: así
     SUMAR.SI(A:A;"x";Enero!B:B) compara posiciones parejas, como en Excel,
     sin recorrer el millón de filas de Excel. */
  let tamLibro = null;
  const tamDelLibro = () => {
    if (!tamLibro) {
      tamLibro = { filas: 1, cols: 1 };
      for (const clave of ["", claveActual, ...mapa.keys()]) {
        const d = dims(clave);
        tamLibro.filas = Math.max(tamLibro.filas, d.filas);
        tamLibro.cols = Math.max(tamLibro.cols, d.cols);
      }
    }
    return tamLibro;
  };

  /* Las esquinas de un rango (0-based) y su hoja. */
  function esquinas(nodo) {
    const clave = claveDe(nodo.hoja);
    // A:XFD o 1:1048576 también se acotan: más allá de lo usado es todo vacío.
    const hasta = (desde, fin, tope) => Math.max(desde, Math.min(fin, tope - 1));
    if (nodo.eje === "col") {
      const ca = letraACol(nodo.a);
      const cb = letraACol(nodo.b);
      const c0 = Math.min(ca, cb);
      return { clave, r0: 0, r1: tamDelLibro().filas - 1, c0, c1: hasta(c0, Math.max(ca, cb), tamDelLibro().cols) };
    }
    if (nodo.eje === "fila") {
      const fa = parseInt(nodo.a, 10) - 1;
      const fb = parseInt(nodo.b, 10) - 1;
      if (fa < 0 || fb < 0) throw new ErrCelda("#REF"); // la fila 0 no existe
      const r0 = Math.min(fa, fb);
      return { clave, r0, r1: hasta(r0, Math.max(fa, fb), tamDelLibro().filas), c0: 0, c1: tamDelLibro().cols - 1 };
    }
    const a = parseRef(nodo.a);
    const b = parseRef(nodo.b);
    if (!a || !b) throw new ErrCelda("#REF");
    return { clave, r0: Math.min(a.fila, b.fila), r1: Math.max(a.fila, b.fila), c0: Math.min(a.col, b.col), c1: Math.max(a.col, b.col) };
  }

  function rangoCeldas(nodo) {
    if (nodo.tipo !== "rango") throw new ErrCelda("#VALOR");
    const { clave, r0, r1, c0, c1 } = esquinas(nodo);
    if (celdasDe(clave) == null) throw new ErrCelda("#REF"); // hoja inexistente
    const m = [];
    const flat = [];
    for (let r = r0; r <= r1; r += 1) {
      const fila = [];
      for (let c = c0; c <= c1; c += 1) {
        const v = valor(clave, r, c); // valor() ya devuelve "" fuera de la hoja
        fila.push(v); flat.push(v);
      }
      m.push(fila);
    }
    return { m, flat };
  }

  /* El número de una celda de un rango para sumar/contar, o null si no
     cuenta (vacía, texto). Una hora «08:30» cuenta, como en Excel, y se anota
     en `horas.n` para que el total también se vea como hora. */
  function numeroDeCelda(v, horas) {
    if (typeof v === "number") return v;
    const h = typeof v === "string" ? horaASerial(v) : null;
    if (h !== null && horas) horas.n += 1;
    return h;
  }
  /* Si TODO lo que se juntó eran horas, el resultado se muestra como hora
     (SUMA de las horas del mes → «187:30»); si se mezcló con números, número. */
  const comoHora = (x, horas, cuantos) => (horas.n > 0 && horas.n === cuantos ? serialAHora(x) : x);

  /* Los números de un nodo, para las funciones de agregación: un rango se
     expande e ignora vacíos y texto; un escalar es un solo número. `horas`
     (opcional, { n }) cuenta cuántos eran horas. */
  function numerosDe(nodo, horas) {
    const h = horas && typeof horas === "object" ? horas : null; // flatMap pasa el índice
    if (nodo.tipo === "rango") {
      const { flat } = rangoCeldas(nodo);
      const out = [];
      for (const v of flat) {
        chk(v);
        const x = numeroDeCelda(v, h);
        if (x !== null) out.push(x);
      }
      return out;
    }
    const v = evalNodo(nodo);
    if (h && textoASerial(v)?.tipo === "hora") h.n += 1;
    return [aNum(v)];
  }
  const sumar = (xs) => xs.reduce((a, b) => a + b, 0);

  /* Un criterio ("Juan", ">5", "<=10", "<>x", "Pe*", "=" para las vacías) →
     un predicado sobre un valor. El número del criterio se lee con la regla
     de acá: ">=1.000" es mil. */
  function criterio(nodo) {
    const cv = evalNodo(nodo);
    let op = "=";
    let operando = cv;
    if (typeof cv === "string") {
      const m = /^(<=|>=|<>|=|<|>)?\s*([\s\S]*)$/.exec(cv.trim());
      op = m[1] || "=";
      const rest = m[2].trim();
      const n = numeroLiteral(rest);
      operando = n !== null ? n : (rest !== "" && !Number.isNaN(Number(rest))) ? Number(rest) : rest;
    }
    const re = (op === "=" || op === "<>") ? comodin(operando) : null;
    if (re) {
      return (val) => {
        if (typeof val === "string" && val.startsWith("#")) return false;
        return calzaComodin(re, val) === (op === "=");
      };
    }
    // ">=10" mira sólo números (y fechas u horas, que valen uno) y ">m" sólo
    // texto, como en Excel: si no, «Pérez» >= 10 daba verdadero (en texto,
    // "P" va después de "1") y CONTAR.SI(">=10") contaba los nombres.
    const esNum = (v) => typeof v === "number" || textoASerial(v) !== null;
    const orden = op !== "=" && op !== "<>";
    return (val) => {
      if (orden && (val === "" || esNum(val) !== esNum(operando))) return false;
      try { return cumpleCmp(comparar(val, operando), op); } catch { return false; }
    };
  }

  function condicionalSuma(args, promedio) {
    if (args.length < 2) throw new ErrCelda("#ERROR");
    const rc = rangoCeldas(args[0]);
    const ok = criterio(args[1]);
    const destino = args[2] ? rangoCeldas(args[2]).flat : rc.flat;
    const horas = { n: 0 };
    let suma = 0;
    let cuenta = 0;
    for (let i = 0; i < rc.flat.length; i += 1) {
      if (ok(rc.flat[i])) {
        const x = numeroDeCelda(destino[i], horas);
        if (x !== null) { suma += x; cuenta += 1; }
      }
    }
    if (promedio) { if (!cuenta) throw new ErrCelda("#DIV/0"); return comoHora(suma / cuenta, horas, cuenta); }
    return comoHora(suma, horas, cuenta);
  }

  function buscarV(args) {
    if (args.length < 3) throw new ErrCelda("#ERROR");
    const buscado = evalNodo(args[0]);
    const { m } = rangoCeldas(args[1]);
    const idx = aNum(evalNodo(args[2]));
    const aprox = args[3] ? aBool(evalNodo(args[3])) : false;
    if (!m.length || idx < 1 || idx > m[0].length) throw new ErrCelda("#REF");
    if (aprox) {
      let elegida = -1;
      for (let r = 0; r < m.length; r += 1) if (comparar(m[r][0], buscado) <= 0) elegida = r;
      if (elegida < 0) throw new ErrCelda("#N/D");
      return m[elegida][idx - 1];
    }
    const igual = igualA(buscado, true);
    for (let r = 0; r < m.length; r += 1) if (igual(m[r][0])) return m[r][idx - 1];
    throw new ErrCelda("#N/D");
  }

  /* Los pares (rango, criterio) de las funciones .CONJUNTO, desde `desde`.
     Devuelve el predicado «la posición i cumple TODOS los criterios». */
  function conjunto(args, desde) {
    const pares = [];
    for (let i = desde; i + 1 < args.length; i += 2) {
      pares.push({ flat: rangoCeldas(args[i]).flat, ok: criterio(args[i + 1]) });
    }
    if (!pares.length) throw new ErrCelda("#ERROR");
    // `?? ""`: con columnas enteras de hojas de distinto alto, lo que falta es vacío.
    return (i) => pares.every((p) => p.ok(p.flat[i] ?? ""));
  }
  /* SUMAR/PROMEDIO/MAX/MIN .SI.CONJUNTO: los números del primer rango en las
     posiciones que cumplen todos los criterios. */
  function numerosDelConjunto(args, horas) {
    if (args.length < 3) throw new ErrCelda("#ERROR");
    const destino = rangoCeldas(args[0]).flat;
    const cumple = conjunto(args, 1);
    const out = [];
    for (let i = 0; i < destino.length; i += 1) {
      if (!cumple(i)) continue;
      const x = numeroDeCelda(destino[i], horas);
      if (x !== null) out.push(x);
    }
    return out;
  }

  /* Un nodo como matriz: un rango tal cual, un valor suelto como 1×1. */
  const matrizDe = (nodo) => (nodo.tipo === "rango" ? rangoCeldas(nodo).m : [[evalNodo(nodo)]]);
  const listaDe = (nodo) => (nodo.tipo === "rango" ? rangoCeldas(nodo).flat : [evalNodo(nodo)]);

  /* Posición (desde 1) de `buscado` en una lista. tipo 0: exacto; 1: el mayor
     que no lo pasa (lista ordenada de menor a mayor); -1: el menor que no queda
     por debajo (lista ordenada de mayor a menor). 0 si no está. */
  /* «Es igual a lo buscado», para las búsquedas exactas. Con `comodines`
     (BUSCARV, COINCIDIR, BUSCARH; no BUSCARX, que en Excel los pide aparte),
     un "Pe*" buscado calza con «Pérez». */
  function igualA(buscado, comodines) {
    const re = comodines ? comodin(buscado) : null;
    if (re) return (v) => { chk(v); return calzaComodin(re, v); };
    return (v) => comparar(v, buscado) === 0;
  }
  function posicion(buscado, lista, tipo, comodines = true) {
    if (tipo === 0) {
      const igual = igualA(buscado, comodines);
      for (let i = 0; i < lista.length; i += 1) if (lista[i] !== "" && igual(lista[i])) return i + 1;
      return 0;
    }
    let elegida = 0;
    for (let i = 0; i < lista.length; i += 1) {
      if (lista[i] === "") continue;
      const cmp = comparar(lista[i], buscado);
      if (tipo > 0 ? cmp <= 0 : cmp >= 0) elegida = i + 1;
    }
    return elegida;
  }

  /* TEXTO(valor; "formato"): fechas ("dd/mm/aaaa", "mm/aaaa", "mmmm") y números
     ("0", "0,00", "#.##0,00", "0%"). Acepta el formato escrito a la inglesa o a
     la nuestra: lo que cuenta es cuántos decimales pide y si separa miles. */
  const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  function textoConFormato(v, formato) {
    const f = String(formato);
    if (/[dmya]/i.test(f) && !/[0#]/.test(f)) {
      const p = partesFecha(typeof v === "number" ? v : serialDe({ tipo: "str", v: aTxt(v) }));
      return f.replace(/a{4}|y{4}|a{2}|y{2}|m{4}|m{3}|m{1,2}|d{1,2}/gi, (tk) => {
        const k = tk.toLowerCase();
        if (k === "aaaa" || k === "yyyy") return String(p.a);
        if (k === "aa" || k === "yy") return dosDig(p.a % 100);
        if (k === "mmmm") return MESES[p.m - 1];
        if (k === "mmm") return MESES[p.m - 1].slice(0, 3);
        if (k === "mm") return dosDig(p.m);
        if (k === "m") return String(p.m);
        if (k === "dd") return dosDig(p.d);
        return String(p.d);
      });
    }
    let n = aNum(v);
    const pct = f.includes("%");
    if (pct) n *= 100;
    const m = /[.,](0+)\s*%?$/.exec(f.replace(/[^0#.,%]/g, ""));
    const dec = m ? m[1].length : 0;
    const miles = /#[.,]#/.test(f);
    const txt = n.toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec, useGrouping: miles });
    return pct ? txt + "%" : txt;
  }

  const serialDeYMD = (a, m0, d) => Math.round((Date.UTC(a, m0, d) - EPOCH_FECHA) / 86400000);
  /* Corre una fecha `meses` meses. Si el día no existe en el mes de llegada
     (31 de enero + 1 mes) queda en el último de ese mes. */
  function correrMeses(serial, meses, alFinal) {
    const p = partesFecha(serial);
    const ultimo = partesFecha(serialDeYMD(p.a, p.m - 1 + meses + 1, 0)).d;
    return serialDeYMD(p.a, p.m - 1 + meses, alFinal ? ultimo : Math.min(p.d, ultimo));
  }

  const req = (args, n) => { if (args.length < n) throw new ErrCelda("#ERROR"); };
  const un = (a) => aNum(evalNodo(a));
  const txt = (a) => aTxt(evalNodo(a));
  /* Un nodo a serial de fecha: si ya es número, es el serial; si es texto
     DD/MM/YYYY, se convierte; si no, #VALOR. */
  const serialDe = (a) => {
    const v = evalNodo(a);
    if (typeof v === "number") return v;
    const ts = textoASerial(aTxt(v)); // también «21/09/2026 08:30», como da AHORA()
    if (ts === null) throw new ErrCelda("#VALOR");
    return ts.serial;
  };
  /* Los segundos del día de un valor con hora (08:30, «21/09/2026 08:30», o
     el número de Excel), para HORA/MINUTO/SEGUNDO: la parte de la fecha no
     cuenta y una duración de «26:30» da la hora 2, como en Excel. */
  const segundosDelDia = (a) => {
    const x = un(a);
    if (x < 0) throw new ErrCelda("#VALOR");
    return Math.round((x - Math.floor(x)) * 86400) % 86400;
  };

  function llamarFn(nombre, args) {
    switch (nombre) {
      case "SUMA": case "SUM": case "PROMEDIO": case "AVERAGE": case "MIN": case "MAX": {
        const horas = { n: 0 };
        const xs = args.flatMap((a) => numerosDe(a, horas));
        let r;
        if (nombre === "SUMA" || nombre === "SUM") r = sumar(xs);
        else if (nombre === "MIN") r = xs.length ? Math.min(...xs) : 0;
        else if (nombre === "MAX") r = xs.length ? Math.max(...xs) : 0;
        else { if (!xs.length) throw new ErrCelda("#DIV/0"); r = sumar(xs) / xs.length; }
        return comoHora(r, horas, xs.length);
      }
      case "CONTAR": case "COUNT": return args.flatMap(numerosDe).length;
      case "CONTARA": case "COUNTA":
        return args.reduce((n, a) => {
          if (a.tipo === "rango") return n + rangoCeldas(a).flat.filter((v) => v !== "").length;
          return n + (evalNodo(a) === "" ? 0 : 1);
        }, 0);
      case "REDONDEAR": case "ROUND":
        req(args, 1);
        return redondearComo(un(args[0]), args[1] ? un(args[1]) : 0, "mitad");
      case "ABS": req(args, 1); return Math.abs(un(args[0]));
      case "ENTERO": case "INT": req(args, 1); return Math.floor(un(args[0]));
      case "RESIDUO": case "MOD": {
        req(args, 2);
        const b = un(args[1]);
        if (b === 0) throw new ErrCelda("#DIV/0");
        const a = un(args[0]);
        return a - b * Math.floor(a / b);
      }
      case "SI": case "IF":
        req(args, 2);
        return aBool(evalNodo(args[0])) ? evalNodo(args[1]) : (args[2] ? evalNodo(args[2]) : false);
      case "Y": case "AND": return args.every((a) => aBool(evalNodo(a)));
      case "O": case "OR": return args.some((a) => aBool(evalNodo(a)));
      case "NO": case "NOT": req(args, 1); return !aBool(evalNodo(args[0]));
      case "SI.ERROR": case "IFERROR": {
        req(args, 2);
        try { return chk(evalNodo(args[0])); } catch (e) {
          if (e instanceof ErrCelda && e.message !== CARGANDO) return evalNodo(args[1]);
          throw e;
        }
      }
      case "SUMAR.SI": case "SUMIF": return condicionalSuma(args, false);
      case "PROMEDIO.SI": case "AVERAGEIF": return condicionalSuma(args, true);
      case "CONTAR.SI": case "COUNTIF": {
        req(args, 2);
        const { flat } = rangoCeldas(args[0]);
        const ok = criterio(args[1]);
        return flat.filter(ok).length;
      }
      case "BUSCARV": case "VLOOKUP": return buscarV(args);
      case "CONCATENAR": case "CONCATENATE": case "CONCAT":
        return args.map((a) => (a.tipo === "rango" ? rangoCeldas(a).flat.map(aTxt).join("") : aTxt(evalNodo(a)))).join("");
      case "MAYUSC": case "UPPER": req(args, 1); return txt(args[0]).toUpperCase();
      case "MINUSC": case "LOWER": req(args, 1); return txt(args[0]).toLowerCase();
      case "LARGO": case "LEN": req(args, 1); return txt(args[0]).length;
      case "IZQUIERDA": case "LEFT": {
        req(args, 1);
        const n = args[1] ? un(args[1]) : 1;
        return txt(args[0]).slice(0, Math.max(0, n));
      }
      case "DERECHA": case "RIGHT": {
        req(args, 1);
        const n = args[1] ? un(args[1]) : 1;
        return n <= 0 ? "" : txt(args[0]).slice(-n);
      }
      case "ESPACIOS": case "TRIM": req(args, 1); return txt(args[0]).trim().replace(/\s+/g, " ");
      case "HOY": case "TODAY": return fechaHoy(false);
      case "AHORA": case "NOW": return fechaHoy(true);
      case "DIAS": case "DAYS": req(args, 2); return Math.floor(serialDe(args[0]) + 1e-9) - Math.floor(serialDe(args[1]) + 1e-9);
      case "FECHA": case "DATE": {
        req(args, 3);
        const ms = Date.UTC(un(args[0]), un(args[1]) - 1, un(args[2]));
        return serialAFecha(Math.round((ms - EPOCH_FECHA) / 86400000));
      }
      case "FECHA.SUMA": case "SUMARDIAS": req(args, 2); return serialAFecha(serialDe(args[0]) + un(args[1]));
      case "AÑO": case "ANIO": case "YEAR": req(args, 1); return partesFecha(serialDe(args[0])).a;
      case "MES": case "MONTH": req(args, 1); return partesFecha(serialDe(args[0])).m;
      case "DIA": case "DAY": req(args, 1); return partesFecha(serialDe(args[0])).d;
      case "DIA.SEM": case "WEEKDAY": {
        // El 2º argumento es el tipo de Excel: 1 (o nada) domingo=1 … sábado=7;
        // 2 lunes=1 … domingo=7; 3 lunes=0 … domingo=6; 11…17 valen 1 el lunes
        // (11), el martes (12) … el domingo (17). Otro tipo es #NUM, como allá.
        req(args, 1);
        const sem = partesFecha(serialDe(args[0])).sem; // 0 domingo … 6 sábado
        const tipo = args[1] ? Math.trunc(un(args[1])) : 1;
        if (tipo === 3) return (sem + 6) % 7;
        let arranca; // el día (0 domingo … 6 sábado) que vale 1
        if (tipo === 1) arranca = 0;
        else if (tipo === 2) arranca = 1;
        else if (tipo >= 11 && tipo <= 17) arranca = (tipo - 10) % 7;
        else throw new ErrCelda("#NUM");
        return ((sem - arranca + 7) % 7) + 1;
      }
      case "HORA": case "HOUR": req(args, 1); return Math.floor(segundosDelDia(args[0]) / 3600);
      case "MINUTO": case "MINUTE": req(args, 1); return Math.floor((segundosDelDia(args[0]) % 3600) / 60);
      case "SEGUNDO": case "SECOND": req(args, 1); return segundosDelDia(args[0]) % 60;
      case "NSHORA": case "TIME": {
        // Como Excel: da la vuelta en 24 (NSHORA(25;0;0) es 01:00) y los
        // minutos y segundos de más se pasan (NSHORA(8;90;0) es 09:30).
        req(args, 3);
        const seg = Math.floor(un(args[0])) * 3600 + Math.floor(un(args[1])) * 60 + Math.floor(un(args[2]));
        if (seg < 0) throw new ErrCelda("#VALOR");
        return serialAHora((seg % 86400) / 86400);
      }

      /* ── Matemáticas ─────────────────────────────────────────────────────── */
      case "PRODUCTO": case "PRODUCT": return args.flatMap(numerosDe).reduce((a, b) => a * b, 1);
      case "POTENCIA": case "POWER": req(args, 2); return un(args[0]) ** un(args[1]);
      case "RAIZ": case "SQRT": {
        req(args, 1);
        const x = un(args[0]);
        if (x < 0) throw new ErrCelda("#VALOR");
        return Math.sqrt(x);
      }
      case "PI": return Math.PI;
      // Como función también: LibreOffice guarda así los booleanos en un .xlsx
      // (BUSCARV(…;FALSE())). Valen lo mismo que la palabra suelta.
      case "VERDADERO": case "TRUE": return VERDAD.VERDADERO;
      case "FALSO": case "FALSE": return VERDAD.FALSO;
      case "TRUNCAR": case "TRUNC": case "REDONDEAR.MENOS": case "ROUNDDOWN":
        req(args, 1);
        return redondearComo(un(args[0]), args[1] ? un(args[1]) : 0, "abajo");
      case "REDONDEAR.MAS": case "ROUNDUP": // se aleja del cero, como Excel
        req(args, 1);
        return redondearComo(un(args[0]), args[1] ? un(args[1]) : 0, "arriba");
      case "MEDIANA": case "MEDIAN": {
        const xs = args.flatMap(numerosDe).sort((a, b) => a - b);
        if (!xs.length) throw new ErrCelda("#DIV/0");
        const m = xs.length >> 1;
        return xs.length % 2 ? xs[m] : (xs[m - 1] + xs[m]) / 2;
      }
      case "K.ESIMO.MAYOR": case "LARGE": case "K.ESIMO.MENOR": case "SMALL": {
        req(args, 2);
        const xs = numerosDe(args[0]).sort((a, b) => a - b);
        const k = un(args[1]);
        if (k < 1 || k > xs.length) throw new ErrCelda("#VALOR");
        return (nombre === "K.ESIMO.MAYOR" || nombre === "LARGE") ? xs[xs.length - k] : xs[k - 1];
      }
      case "SUMAPRODUCTO": case "SUMPRODUCT": {
        req(args, 1);
        const listas = args.map(listaDe);
        if (listas.some((l) => l.length !== listas[0].length)) throw new ErrCelda("#VALOR");
        let suma = 0;
        for (let i = 0; i < listas[0].length; i += 1) {
          suma += listas.reduce((p, l) => p * (typeof chk(l[i]) === "number" ? l[i] : 0), 1);
        }
        return suma;
      }
      case "CONTAR.BLANCO": case "COUNTBLANK":
        req(args, 1); return listaDe(args[0]).filter((v) => v === "").length;

      /* ── Condicionales con varios criterios ──────────────────────────────── */
      case "SUMAR.SI.CONJUNTO": case "SUMIFS": {
        const horas = { n: 0 };
        const xs = numerosDelConjunto(args, horas);
        return comoHora(sumar(xs), horas, xs.length);
      }
      case "PROMEDIO.SI.CONJUNTO": case "AVERAGEIFS": {
        const horas = { n: 0 };
        const xs = numerosDelConjunto(args, horas);
        if (!xs.length) throw new ErrCelda("#DIV/0");
        return comoHora(sumar(xs) / xs.length, horas, xs.length);
      }
      case "MAX.SI.CONJUNTO": case "MAXIFS": case "MIN.SI.CONJUNTO": case "MINIFS": {
        const horas = { n: 0 };
        const xs = numerosDelConjunto(args, horas);
        const max = nombre === "MAX.SI.CONJUNTO" || nombre === "MAXIFS";
        return comoHora(xs.length ? (max ? Math.max(...xs) : Math.min(...xs)) : 0, horas, xs.length);
      }
      case "CONTAR.SI.CONJUNTO": case "COUNTIFS": {
        req(args, 2);
        const n = rangoCeldas(args[0]).flat.length;
        const cumple = conjunto(args, 0);
        let cuenta = 0;
        for (let i = 0; i < n; i += 1) if (cumple(i)) cuenta += 1;
        return cuenta;
      }

      /* ── Lógicas y de información ────────────────────────────────────────── */
      case "SI.CONJUNTO": case "IFS": {
        for (let i = 0; i + 1 < args.length; i += 2) if (aBool(evalNodo(args[i]))) return evalNodo(args[i + 1]);
        throw new ErrCelda("#N/D");
      }
      case "SI.ND": case "IFNA": {
        req(args, 2);
        try { return chk(evalNodo(args[0])); } catch (e) {
          if (e instanceof ErrCelda && e.message === "#N/D") return evalNodo(args[1]);
          throw e;
        }
      }
      case "ESERROR": case "ISERROR": {
        req(args, 1);
        try { chk(evalNodo(args[0])); return false; } catch (e) {
          if (e instanceof ErrCelda && e.message !== CARGANDO) return true;
          throw e;
        }
      }
      case "ESBLANCO": case "ISBLANK": req(args, 1); return evalNodo(args[0]) === "";
      case "ESNUMERO": case "ISNUMBER": req(args, 1); return typeof evalNodo(args[0]) === "number";
      case "ESTEXTO": case "ISTEXT": {
        req(args, 1);
        const v = evalNodo(args[0]);
        return typeof v === "string" && v !== "" && !v.startsWith("#");
      }

      /* ── Búsqueda ────────────────────────────────────────────────────────── */
      case "COINCIDIR": case "MATCH": {
        req(args, 2);
        const tipo = args[2] ? un(args[2]) : 1;
        const pos = posicion(chk(evalNodo(args[0])), listaDe(args[1]), tipo);
        if (!pos) throw new ErrCelda("#N/D");
        return pos;
      }
      case "INDICE": case "INDEX": {
        req(args, 2);
        const m = matrizDe(args[0]);
        let f = un(args[1]);
        let c = args[2] ? un(args[2]) : 1;
        // Un rango de una sola fila con un solo número: ese número es la columna.
        if (!args[2] && m.length === 1) { c = f; f = 1; }
        if (f < 1 || c < 1 || f > m.length || c > m[0].length) throw new ErrCelda("#REF");
        return m[f - 1][c - 1];
      }
      case "BUSCARX": case "XLOOKUP": {
        req(args, 3);
        const pos = posicion(chk(evalNodo(args[0])), listaDe(args[1]), 0, false);
        if (!pos) { if (args[3]) return evalNodo(args[3]); throw new ErrCelda("#N/D"); }
        const dev = listaDe(args[2]);
        if (pos > dev.length) throw new ErrCelda("#REF");
        return dev[pos - 1];
      }
      case "BUSCARH": case "HLOOKUP": {
        req(args, 3);
        const { m } = rangoCeldas(args[1]);
        const idx = un(args[2]);
        if (!m.length || idx < 1 || idx > m.length) throw new ErrCelda("#REF");
        const pos = posicion(chk(evalNodo(args[0])), m[0], args[3] && aBool(evalNodo(args[3])) ? 1 : 0);
        if (!pos) throw new ErrCelda("#N/D");
        return m[idx - 1][pos - 1];
      }

      /* ── Texto ───────────────────────────────────────────────────────────── */
      case "EXTRAE": case "MED": case "MID": {
        req(args, 3);
        const ini = un(args[1]);
        const n = un(args[2]);
        if (ini < 1 || n < 0) throw new ErrCelda("#VALOR");
        return txt(args[0]).slice(ini - 1, ini - 1 + n);
      }
      case "HALLAR": case "SEARCH": case "ENCONTRAR": case "FIND": {
        req(args, 2);
        const exacto = nombre === "ENCONTRAR" || nombre === "FIND";
        const aguja = exacto ? txt(args[0]) : txt(args[0]).toLowerCase();
        const pajar = exacto ? txt(args[1]) : txt(args[1]).toLowerCase();
        const pos = pajar.indexOf(aguja, (args[2] ? un(args[2]) : 1) - 1);
        if (pos < 0) throw new ErrCelda("#VALOR");
        return pos + 1;
      }
      case "SUSTITUIR": case "SUBSTITUTE": {
        req(args, 3);
        const base = txt(args[0]);
        const viejo = txt(args[1]);
        const nuevo = txt(args[2]);
        if (viejo === "") return base;
        if (!args[3]) return base.split(viejo).join(nuevo);
        // Con el cuarto argumento, sólo esa aparición.
        const cual = un(args[3]);
        let desde = -1;
        for (let k = 0; k < cual; k += 1) {
          desde = base.indexOf(viejo, desde + 1);
          if (desde < 0) return base;
        }
        return base.slice(0, desde) + nuevo + base.slice(desde + viejo.length);
      }
      case "NOMPROPIO": case "PROPER":
        req(args, 1);
        return txt(args[0]).toLowerCase().replace(/(^|[^\p{L}])(\p{L})/gu, (_, a, b) => a + b.toUpperCase());
      case "REPETIR": case "REPT": req(args, 2); return txt(args[0]).repeat(Math.max(0, Math.floor(un(args[1]))));
      case "VALOR": case "VALUE": req(args, 1); return un(args[0]);
      case "TEXTO": case "TEXT": {
        req(args, 2);
        // El formato se lee crudo: "#.##0,00" empieza con # y `txt` lo tomaría
        // por un error a propagar.
        const formato = evalNodo(args[1]);
        return textoConFormato(chk(evalNodo(args[0])), typeof formato === "string" ? formato : aTxt(formato));
      }
      case "UNIRCADENAS": case "TEXTJOIN": {
        req(args, 3);
        const sep = txt(args[0]);
        const sinVacios = aBool(evalNodo(args[1]));
        const partes = args.slice(2).flatMap(listaDe).map(aTxt);
        return (sinVacios ? partes.filter((x) => x !== "") : partes).join(sep);
      }

      /* ── Fechas ──────────────────────────────────────────────────────────── */
      case "FIN.MES": case "EOMONTH":
        req(args, 2); return serialAFecha(correrMeses(serialDe(args[0]), un(args[1]), true));
      case "FECHA.MES": case "EDATE":
        req(args, 2); return serialAFecha(correrMeses(serialDe(args[0]), un(args[1]), false));
      case "DIAS.LAB": case "NETWORKDAYS": {
        req(args, 2);
        const a = serialDe(args[0]);
        const b = serialDe(args[1]);
        // El tercer argumento, si está, son los feriados (un rango de fechas).
        const feriados = new Set(args[2]
          ? listaDe(args[2]).map((v) => (typeof v === "number" ? v : fechaASerial(String(v)))).filter((x) => x != null)
          : []);
        let cuenta = 0;
        for (let d = Math.min(a, b); d <= Math.max(a, b); d += 1) {
          const sem = partesFecha(d).sem;
          if (sem !== 0 && sem !== 6 && !feriados.has(d)) cuenta += 1;
        }
        return a <= b ? cuenta : -cuenta;
      }
      case "SIFECHA": case "DATEDIF": {
        req(args, 3);
        const a = serialDe(args[0]);
        const b = serialDe(args[1]);
        if (b < a) throw new ErrCelda("#VALOR");
        const u = txt(args[2]).toUpperCase();
        if (u === "D") return b - a;
        const pa = partesFecha(a);
        const pb = partesFecha(b);
        const meses = (pb.a - pa.a) * 12 + (pb.m - pa.m) - (pb.d < pa.d ? 1 : 0);
        if (u === "M") return meses;
        if (u === "Y" || u === "A") return Math.floor(meses / 12);
        throw new ErrCelda("#VALOR");
      }
      case "ISO.NUM.DE.SEMANA": case "NUM.DE.SEMANA": case "ISOWEEKNUM": case "WEEKNUM": {
        // La semana ISO: empieza el lunes y la primera es la que tiene el primer
        // jueves del año. Es la que usan las planillas de turnos y de viajes.
        req(args, 1);
        const s = serialDe(args[0]);
        const jueves = s - ((partesFecha(s).sem + 6) % 7) + 3;
        const p = partesFecha(jueves);
        return Math.floor((jueves - serialDeYMD(p.a, 0, 1)) / 7) + 1;
      }
      default:
        if (nombre.startsWith("HUB.")) return llamarHub(nombre.slice(4), args);
        throw new ErrCelda("#NOMBRE");
    }
  }

  /* HUB.…: los argumentos se calculan acá (un error o un CARGANDO de más
     arriba se propaga sin preguntarle nada al hub) y el dato lo contesta
     `opciones.hub`. Un rango no es un argumento válido: cada función del hub
     pide valores sueltos (un interno, una fecha). */
  function llamarHub(nombre, args) {
    if (!hub) {
      throw new ErrCelda(ERROR_HUB, "Los datos del hub se leen con la sesión de quien mira la planilla, y acá no hay ninguna (por ejemplo, en el bot de los avisos por WhatsApp).");
    }
    const valores = args.map((a) => {
      if (a.tipo === "rango") throw new ErrCelda("#VALOR", "Las funciones HUB.… piden valores sueltos, no rangos.");
      return chk(evalNodo(a));
    });
    const r = hub.leer(nombre, valores) || {};
    if (r.cargando) throw new ErrCelda(CARGANDO);
    if (r.error) throw new ErrCelda(String(r.error), r.motivo || null);
    return r.valor ?? "";
  }

  function evalNodo(n) {
    switch (n.tipo) {
      case "num": return n.v;
      case "str": return n.v;
      case "neg": return -aNum(evalNodo(n.arg));
      case "pct": return aNum(evalNodo(n.arg)) / 100;
      case "err": throw new ErrCelda(n.v);
      case "cat": return aTxt(evalNodo(n.izq)) + aTxt(evalNodo(n.der));
      case "cmp": return cumpleCmp(comparar(evalNodo(n.izq), evalNodo(n.der)), n.op);
      case "op": {
        const va = evalNodo(n.izq);
        const vb = evalNodo(n.der);
        const a = aNum(va);
        const b = aNum(vb);
        switch (n.op) {
          case "+": return conHora(a + b, "+", va, vb);
          case "-": return conHora(a - b, "-", va, vb);
          case "*": return a * b;
          case "/": if (b === 0) throw new ErrCelda("#DIV/0"); return a / b;
          case "^": return a ** b;
          default: throw new ErrCelda("#ERROR");
        }
      }
      case "ref": {
        // VERDADERO/FALSO sólo cuando NO nombra hoja (una palabra suelta).
        if (n.hoja == null) {
          const up = n.v.toUpperCase();
          if (up in VERDAD) return VERDAD[up];
        }
        const ref = parseRef(n.v);
        if (!ref) throw new ErrCelda("#NOMBRE");
        return valor(claveDe(n.hoja), ref.fila, ref.col); // "" fuera de la hoja, #REF si no existe
      }
      case "rango": throw new ErrCelda("#VALOR");
      case "fn": return llamarFn(n.nombre, n.args);
      default: throw new ErrCelda("#ERROR");
    }
  }

  const { filas, cols } = dims(claveActual);
  const salida = [];
  for (let r = 0; r < filas; r += 1) {
    const filaOut = [];
    for (let c = 0; c < cols; c += 1) {
      const v = valor(claveActual, r, c);
      const k = claveActual + "|" + r + "," + c;
      filaOut.push(vistas.has(k) ? vistas.get(k) : v);
    }
    salida.push(filaOut);
  }
  return salida;
}

/* ── Reescribir las referencias de una fórmula ─────────────────────────────
 *
 * Copiar, rellenar, insertar una fila: todo eso cambia a qué celda apunta una
 * fórmula sin cambiar lo que calcula. Se hace sobre el TEXTO, no sobre el
 * árbol, para devolverla escrita como la escribió la persona (sus espacios,
 * sus minúsculas, su ; o su ,) con sólo las referencias tocadas.
 *
 * `fn` recibe cada referencia —`{ hoja, a, b }`, con `b` si es un rango— y
 * devuelve la nueva, o null si dejó de existir (queda #REF!). Una columna o
 * fila entera (A:C, 3:5) llega como rango con `entera: "col" | "fila"` en sus
 * dos puntas: de la columna sólo importa `col`, de la fila sólo `fila`. Si lo
 * que devuelve trae `hoja` (y es otra), la referencia pasa a esa hoja. */

const RE_CELDA = /^(\$?)([A-Za-z]{1,3})(\$?)(\d{1,7})$/;

function leerCelda(txt) {
  const m = RE_CELDA.exec(txt);
  if (!m) return null;
  return { absC: !!m[1], col: letraACol(m[2]), absF: !!m[3], fila: parseInt(m[4], 10) - 1 };
}

function escribirCelda(p) {
  if (p.entera === "col") return `${p.absC ? "$" : ""}${colALetra(p.col)}`;
  if (p.entera === "fila") return `${p.absF ? "$" : ""}${p.fila + 1}`;
  return `${p.absC ? "$" : ""}${colALetra(p.col)}${p.absF ? "$" : ""}${p.fila + 1}`;
}

/* A:C o 3:5 (con o sin $) al principio de `txt` → el grupo, o null. */
function leerEntera(txt) {
  const pos = (m) => m[0].indexOf(":");
  let m = RE_COLS.exec(txt);
  if (m) {
    const punta = (abs, letras) => ({ entera: "col", absC: abs, col: letraACol(letras), absF: false, fila: 0 });
    return { a: punta(m[0][0] === "$", m[1]), b: punta(m[0][pos(m) + 1] === "$", m[2]), largo: m[0].length };
  }
  m = RE_FILAS.exec(txt);
  if (m) {
    const punta = (abs, n) => ({ entera: "fila", absC: false, col: 0, absF: abs, fila: parseInt(n, 10) - 1 });
    return { a: punta(m[0][0] === "$", m[1]), b: punta(m[0][pos(m) + 1] === "$", m[2]), largo: m[0].length };
  }
  return null;
}

function reescribir(formula, fn) {
  const s = String(formula ?? "");
  if (!s.startsWith("=")) return s;
  const esIdent = (ch) => /[\p{L}\p{N}_.$]/u.test(ch);
  const palabra = (desde) => { let j = desde; while (j < s.length && esIdent(s[j])) j += 1; return j; };
  // Lee `celda`, `celda:celda` o una columna/fila entera a partir de `desde`.
  const leerGrupo = (desde) => {
    const ent = leerEntera(s.slice(desde));
    if (ent) return { a: ent.a, b: ent.b, fin: desde + ent.largo };
    const j = palabra(desde);
    const a = leerCelda(s.slice(desde, j));
    if (!a) return null;
    if (s[j] === ":") {
      const k = palabra(j + 1);
      const b = leerCelda(s.slice(j + 1, k));
      if (b) return { a, b, fin: k };
    }
    return { a, b: null, fin: j };
  };
  let out = "=";
  const emitir = (hoja, prefijo, g) => {
    const r = fn({ hoja, a: g.a, b: g.b });
    const pre = r && r.hoja != null && r.hoja !== hoja ? prefijoDeHoja(r.hoja) : prefijo;
    out += r == null ? "#REF!" : pre + escribirCelda(r.a) + (r.b ? ":" + escribirCelda(r.b) : "");
  };
  let i = 1;
  while (i < s.length) {
    const ch = s[i];
    if (ch === '"') { // un texto: adentro no hay referencias
      let j = i + 1;
      while (j < s.length) {
        if (s[j] === '"') { if (s[j + 1] === '"') { j += 2; continue; } j += 1; break; }
        j += 1;
      }
      out += s.slice(i, j); i = j; continue;
    }
    if (ch === "'") { // 'Mi Hoja'!A1
      let j = i + 1;
      let nombre = "";
      while (j < s.length) {
        if (s[j] === "'") { if (s[j + 1] === "'") { nombre += "'"; j += 2; continue; } j += 1; break; }
        nombre += s[j]; j += 1;
      }
      const g = s[j] === "!" ? leerGrupo(j + 1) : null;
      if (g) { emitir(nombre, s.slice(i, j + 1), g); i = g.fin; continue; }
      out += s.slice(i, j); i = j; continue;
    }
    if (ch >= "0" && ch <= "9") { // un número no es una referencia (3:5 sí)
      const g = leerGrupo(i);
      if (g && g.b && g.a.entera) { emitir(null, "", g); i = g.fin; continue; }
      let j = i + 1;
      while (j < s.length && ((s[j] >= "0" && s[j] <= "9") || s[j] === ".")) j += 1;
      out += s.slice(i, j); i = j; continue;
    }
    if (/[\p{L}_$]/u.test(ch)) {
      const j = palabra(i);
      if (s[j] === "!") { // Enero!A1
        const g = leerGrupo(j + 1);
        if (g) { emitir(s.slice(i, j), s.slice(i, j + 1), g); i = g.fin; continue; }
        out += s.slice(i, j + 1); i = j + 1; continue;
      }
      let k = j;
      while (k < s.length && s[k] === " ") k += 1;
      const g = s[k] === "(" ? null : leerGrupo(i); // LOG10( es una función, no la celda LOG10
      if (g) { emitir(null, "", g); i = g.fin; continue; }
      out += s.slice(i, j); i = j; continue;
    }
    out += ch; i += 1;
  }
  return out;
}

/* «Enero!» o «'Mi Hoja'!»: entre comillas cuando el nombre no es una palabra
   suelta o se confunde con una celda (una hoja «A1» o «Viajes (2)»). */
function prefijoDeHoja(nombre) {
  const n = String(nombre);
  const suelta = /^[\p{L}_][\p{L}\p{N}_.]*$/u.test(n) && !leerCelda(n) && !/^[A-Za-z]{1,3}$/.test(n);
  return suelta ? `${n}!` : `'${n.replace(/'/g, "''")}'!`;
}

/* La fórmula con las hojas renombradas: `mapa` va del nombre viejo (en
   minúsculas, como se buscan las hojas) al nuevo. Las referencias sin hoja,
   o a hojas que no están en el mapa, quedan como estaban. Sirve para sumar
   las hojas de un Excel a una planilla abierta que ya tiene hojas con esos
   nombres (ver `shared/importarExcel`). */
export function renombrarHojasEnFormula(formula, mapa) {
  return reescribir(formula, ({ hoja, a, b }) => {
    const nuevo = hoja == null ? null : mapa.get(String(hoja).toLowerCase());
    return nuevo == null ? { a, b } : { hoja: nuevo, a, b };
  });
}

/* La fórmula como queda al copiarla `df` filas y `dc` columnas más allá: las
   referencias relativas se corren, las fijas ($) no. La que se va de la hoja
   queda #REF!. Lo que no es fórmula vuelve igual. */
export function desplazarFormula(formula, df, dc) {
  const mover = (p) => {
    // De una columna entera no se corre la fila (A:A copiada abajo sigue A:A).
    const col = p.absC || p.entera === "fila" ? p.col : p.col + dc;
    const fila = p.absF || p.entera === "col" ? p.fila : p.fila + df;
    return col < 0 || fila < 0 ? null : { ...p, col, fila };
  };
  return reescribir(formula, ({ a, b }) => {
    const na = mover(a);
    const nb = b ? mover(b) : null;
    return !na || (b && !nb) ? null : { a: na, b: nb };
  });
}

/* La fórmula como queda cuando en la hoja `cambio.hoja` se insertan
 * (`cuantas` > 0) o se borran (`cuantas` < 0) filas o columnas a partir de
 * `desde`. `hojaFormula` es la hoja donde vive la fórmula: una referencia sin
 * nombre de hoja apunta a esa.
 *
 * Como en Excel: lo que está más allá se corre; un rango que contiene el lugar
 * se agranda o se achica; una referencia a algo borrado queda #REF!. Acá el $
 * no cuenta: fija al copiar, no al mover la hoja de abajo. */
export function ajustarFormula(formula, cambio) {
  const { eje, desde, cuantas } = cambio;
  const afectada = String(cambio.hoja ?? "").toLowerCase();
  const propia = String(cambio.hojaFormula ?? cambio.hoja ?? "").toLowerCase();
  const k = eje === "col" ? "col" : "fila";
  const borradas = cuantas < 0 ? -cuantas : 0;
  const ultimaBorrada = desde + borradas - 1;
  const punto = (v) => {
    if (cuantas > 0) return v >= desde ? v + cuantas : v;
    if (v < desde) return v;
    return v > ultimaBorrada ? v - borradas : null;
  };
  return reescribir(formula, ({ hoja, a, b }) => {
    if ((hoja == null ? propia : String(hoja).toLowerCase()) !== afectada) return { a, b };
    if (a.entera && a.entera !== k) return { a, b }; // insertar filas no toca A:C
    if (!b) {
      const n = punto(a[k]);
      return n == null ? null : { a: { ...a, [k]: n }, b: null };
    }
    let lo = Math.min(a[k], b[k]);
    let hi = Math.max(a[k], b[k]);
    if (cuantas > 0) {
      lo = punto(lo); hi = punto(hi);
    } else {
      if (lo >= desde && hi <= ultimaBorrada) return null; // se borró el rango entero
      const nlo = lo < desde ? lo : (lo > ultimaBorrada ? lo - borradas : desde);
      const nhi = hi < desde ? hi : (hi > ultimaBorrada ? hi - borradas : desde - 1);
      lo = nlo; hi = nhi;
    }
    const aEsMenor = a[k] <= b[k];
    return { a: { ...a, [k]: aEsMenor ? lo : hi }, b: { ...b, [k]: aEsMenor ? hi : lo } };
  });
}
